import React, { useMemo } from "react";
import { useHSE } from "../lib/store";
import { StatCard, PRIORITY_BADGES } from "../components/RegisterPage";
import { AlertOctagon, BellRing, CalendarX, GraduationCap, FileBadge, Scale, FolderCog, FileX2 } from "lucide-react";
import { getExpiryStatus, calculateDaysOverdue } from "../lib/hseCalc";

const ICONS = {
  "Overdue Action": AlertOctagon,
  "Expired Training": GraduationCap,
  "Expiring Training": GraduationCap,
  "Expired Certificate": CalendarX,
  "Expiring Certificate": CalendarX,
  "Overdue NCR": FileX2,
  "Overdue SOR": AlertOctagon,
  "Document Review Due": FolderCog,
  "Legal Review Due": Scale,
  "Expiring Permit": FileBadge,
};

const SEV_COLOR = {
  Critical: "#EF4444",
  High: "#F59E0B",
  Medium: "#2563EB",
  Low: "#18B84F",
};

export default function Notifications() {
  const { data } = useHSE();

  // Compute real-time notifications from register data.
  const live = useMemo(() => {
    const list = [];
    (data.actions || []).forEach((a) => {
      const over = calculateDaysOverdue(a.dueDate, a.status);
      if (over > 0 && a.status !== "Closed") {
        list.push({
          id: `NTF-A-${a.id}`, type: "Overdue Action", refId: a.id,
          severity: over > 30 ? "Critical" : "High",
          message: `Action overdue by ${over} days: ${a.description || a.id}`,
          date: a.dueDate,
        });
      }
    });
    (data.trainings || []).forEach((t) => {
      const s = getExpiryStatus(t.expiryDate);
      if (s === "Expired") {
        list.push({ id: `NTF-T-${t.id}`, type: "Expired Training", refId: t.id, severity: "High",
          message: `Training expired: ${t.type} (${t.employeeName})`, date: t.expiryDate });
      } else if (s === "Expiring Soon") {
        list.push({ id: `NTF-T-${t.id}`, type: "Expiring Training", refId: t.id, severity: "Medium",
          message: `Training expiring in <30 days: ${t.type} (${t.employeeName})`, date: t.expiryDate });
      }
    });
    (data.equipment || []).forEach((e) => {
      const s = getExpiryStatus(e.expiryDate);
      if (s === "Expired") {
        list.push({ id: `NTF-E-${e.id}`, type: "Expired Certificate", refId: e.id, severity: "Critical",
          message: `Certificate expired: ${e.type} (${e.serial})`, date: e.expiryDate });
      } else if (s === "Expiring Soon") {
        list.push({ id: `NTF-E-${e.id}`, type: "Expiring Certificate", refId: e.id, severity: "High",
          message: `Certificate expiring in <30 days: ${e.type} (${e.serial})`, date: e.expiryDate });
      }
    });
    (data.ncrs || []).forEach((n) => {
      if (n.status === "Overdue" || (n.status !== "Closed" && calculateDaysOverdue(n.dueDate, n.status) > 0)) {
        list.push({ id: `NTF-N-${n.id}`, type: "Overdue NCR", refId: n.id, severity: "Critical",
          message: `NCR overdue: ${n.description?.slice(0, 60) || n.id}`, date: n.dueDate });
      }
    });
    (data.observations || []).forEach((o) => {
      if (o.status !== "Closed" && calculateDaysOverdue(o.dueDate, o.status) > 0 && o.sorId) {
        list.push({ id: `NTF-O-${o.id}`, type: "Overdue SOR", refId: o.sorId || o.id, severity: "High",
          message: `SOR overdue: ${o.description?.slice(0, 60) || o.id}`, date: o.dueDate });
      }
    });
    (data.documents || []).forEach((d) => {
      if (d.status === "Draft") {
        list.push({ id: `NTF-D-${d.docNo}`, type: "Document Review Due", refId: d.docNo, severity: "Medium",
          message: `Draft document pending approval: ${d.title}`, date: d.reviewDate });
      }
    });
    (data.legalCompliance || []).forEach((l) => {
      if (l.complianceStatus === "Partial" || l.complianceStatus === "Non-Compliant") {
        list.push({ id: `NTF-L-${l.id}`, type: "Legal Review Due", refId: l.id, severity: "High",
          message: `Legal compliance action required: ${l.regulation} — ${l.clause}`, date: l.reviewDate });
      }
    });
    return list.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [data]);

  const bySev = (s) => live.filter((x) => x.severity === s).length;
  const byType = (t) => live.filter((x) => x.type === t).length;

  return (
    <div className="space-y-5" data-testid="notifications-page">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-[#0E2336] flex items-center gap-2">
          <BellRing className="w-6 h-6 text-[#18B84F]" /> Notifications & Expiry Alerts
        </h1>
        <div className="text-sm text-slate-500 mt-1">
          Live overdue actions, expiring trainings, certificates, permits, NCRs, SORs and document reviews
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Critical" value={bySev("Critical")} tone="critical" testid="ntf-critical" />
        <StatCard label="High" value={bySev("High")} tone="warning" testid="ntf-high" />
        <StatCard label="Medium" value={bySev("Medium")} tone="info" testid="ntf-medium" />
        <StatCard label="Total Alerts" value={live.length} testid="ntf-total" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <MiniStat label="Overdue Actions" value={byType("Overdue Action")} icon={AlertOctagon} />
        <MiniStat label="Expired Trainings" value={byType("Expired Training")} icon={GraduationCap} />
        <MiniStat label="Expiring Trainings" value={byType("Expiring Training")} icon={GraduationCap} />
        <MiniStat label="Expired Certs" value={byType("Expired Certificate")} icon={CalendarX} />
        <MiniStat label="Expiring Certs" value={byType("Expiring Certificate")} icon={CalendarX} />
        <MiniStat label="Overdue NCR" value={byType("Overdue NCR")} icon={FileX2} />
        <MiniStat label="Docs Due" value={byType("Document Review Due")} icon={FolderCog} />
      </div>

      <div className="command-card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="font-heading font-bold text-[#082B4D] text-[13px]">All Active Alerts</div>
          <div className="text-[11px] text-slate-500">{live.length} active</div>
        </div>
        <div className="divide-y divide-slate-100">
          {live.length === 0 && (
            <div className="px-4 py-12 text-center text-slate-400 text-sm">No active alerts — all clear.</div>
          )}
          {live.map((n) => {
            const Icon = ICONS[n.type] || BellRing;
            return (
              <div key={n.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                     style={{ background: `${SEV_COLOR[n.severity]}15`, color: SEV_COLOR[n.severity] }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] text-slate-800 font-medium truncate">{n.message}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    <span className="font-mono text-[#082B4D]">{n.refId}</span> · {n.type} · {n.date}
                  </div>
                </div>
                <span className="cmd-pill shrink-0" style={{ background: `${SEV_COLOR[n.severity]}20`, color: SEV_COLOR[n.severity] }}>
                  {n.severity}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon }) {
  return (
    <div className="command-card p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-[#082B4D]" />
        <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      </div>
      <div className="font-heading font-bold text-xl tabular text-[#0E2336]">{value}</div>
    </div>
  );
}
